import React, { useState, useRef, useEffect } from 'react'
import { Box, Button } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useStyles } from '../styles/CreditCardComparisonStyle'

const MobileCardCarousel = ({ 
  cards, 
  onSelectCard, 
  onCardChange,
  initialActiveIndex = 0
}) => {
  const classes = useStyles()
  const [activeCardIndex, setActiveCardIndex] = useState(initialActiveIndex)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const scrollContainerRef = useRef(null)

  useEffect(() => {
    if (typeof onCardChange === 'function') {
      onCardChange(activeCardIndex)
    }
  }, [activeCardIndex, onCardChange])

  // Track scrolling to update active card index
  const handleScroll = () => {
    if (!scrollContainerRef.current || isDragging) return

    const container = scrollContainerRef.current
    const scrollPosition = container.scrollLeft
    const cardWidth = container.clientWidth
    
    // Calculate which card is most visible
    const index = Math.round(scrollPosition / cardWidth)
    if (index >= 0 && index < cards.length && index !== activeCardIndex) {
      setActiveCardIndex(index)
    }
  }

  // Smooth scroll to a specific card
  const scrollToCard = (index) => {
    if (!scrollContainerRef.current) return
    
    const container = scrollContainerRef.current
    if (index >= 0 && index < cards.length) {
      container.scrollTo({
        left: index * container.clientWidth,
        behavior: 'smooth'
      })
      setActiveCardIndex(index)
    }
  }

  // Handle navigation between cards
  const handlePrevCard = () => {
    scrollToCard(activeCardIndex - 1)
  }

  const handleNextCard = () => {
    scrollToCard(activeCardIndex + 1)
  }

  // Touch and mouse drag handling for better mobile experience
  const handleMouseDown = (e) => {
    setIsDragging(true)
    setStartX(e.clientX || (e.touches && e.touches[0].clientX) || 0)
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return
    
    const x = e.clientX || (e.touches && e.touches[0].clientX) || 0
    const diff = startX - x
    scrollContainerRef.current.scrollLeft += diff
    setStartX(x)
  }

  const handleMouseUp = () => {
    if (!isDragging) return
    setIsDragging(false)
    handleScroll()
  }

  // Get card class based on its position relative to active card
  const getCardClass = (index) => {
    if (index === activeCardIndex) return classes.activeCard
    if (index === activeCardIndex - 1 || index === activeCardIndex + 1) return classes.adjacentCard
    return ''
  }

  return (
    <div className={classes.cardCarouselContainer}>
      {activeCardIndex > 0 && (
        <button 
          className={`${classes.cardNavigationButton} ${classes.prevButton}`}
          onClick={handlePrevCard}
          aria-label="Previous card"
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </button>
      )}
      
      {activeCardIndex < cards.length - 1 && (
        <button 
          className={`${classes.cardNavigationButton} ${classes.nextButton}`}
          onClick={handleNextCard}
          aria-label="Next card"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </button>
      )}
      
      <Box
        ref={scrollContainerRef}
        className={classes.cardGridContainer}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {cards.map((card, index) => (
          <div
            className={`${classes.cardItemContainer} ${getCardClass(index)}`}
            key={card.id}
          >
            <div className={classes.cardImageContainer}>
              <img
                src={card.cardImage}
                alt={`${card.name || 'Credit'} card`}
                className={classes.cardImage}
                width={253}
                height={162}
                onClick={() => scrollToCard(index)}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <Button
              variant="contained"
              className={classes.selectCardButton}
              onClick={() => onSelectCard(card)}
              fullWidth
            >
              Select This Card
            </Button>
          </div>
        ))}
      </Box>
      
      {cards.length > 1 && (
        <Box className={classes.cardIndicators}>
          {cards.map((_, index) => (
            <span
              key={index}
              className={`${classes.cardIndicator} ${activeCardIndex === index ? classes.activeIndicator : ''}`}
              onClick={() => scrollToCard(index)}
            ></span>
          ))}
        </Box>
      )}
    </div>
  )
}

export default MobileCardCarousel
